// useSfx.js
import { useRef, useState, useCallback } from "react";

let SHARED_CTX = null;
let SHARED_MASTER = null;
let SHARED_COMP = null;

export function useSfx({ initialMuted = false } = {}) {
	const ctxRef = useRef(null);
	const [muted, setMuted] = useState(initialMuted);
	const lastTickRef = useRef(0);
	const masterRef = useRef(null);
	const compRef = useRef(null);

	const [volume, setVolume] = useState(0.8); // volume globale 80%

	const ensureAudio = useCallback(() => {
		if (muted) return null;

		const Ctx = window.AudioContext || window.webkitAudioContext;
		if (!Ctx) return null;

		// 🔒 singleton AudioContext
		if (!SHARED_CTX) SHARED_CTX = new Ctx();
		const ctx = SHARED_CTX;
		if (ctx.state === "suspended") ctx.resume();

		// 🔒 singleton master bus + compressor
		if (!SHARED_MASTER) {
			const mg = ctx.createGain();
			mg.gain.value = Math.max(0, Math.min(1, volume));

			const comp = ctx.createDynamicsCompressor();
			comp.threshold.value = -18;
			comp.knee.value = 18;
			comp.ratio.value = 3.5;
			comp.attack.value = 0.003;
			comp.release.value = 0.12;

			mg.connect(comp);
			comp.connect(ctx.destination);

			SHARED_MASTER = mg;
			SHARED_COMP = comp;
		} else {
			SHARED_MASTER.gain.value = Math.max(0, Math.min(1, volume));
		}

		// esponi i riferimenti anche nell’istanza locale
		ctxRef.current = ctx;
		masterRef.current = SHARED_MASTER;
		compRef.current = SHARED_COMP;

		return ctx;
	}, [muted, volume]);

	const blip = (freq = 600, duration = 0.06, type = "square", gain = 0.1) => {
		const ctx = ensureAudio();
		if (!ctx) return;
		const t0 = ctx.currentTime;
		const o = ctx.createOscillator();
		const g = ctx.createGain();
		o.type = type;
		o.frequency.setValueAtTime(freq, t0);
		g.gain.setValueAtTime(0, t0);
		g.gain.linearRampToValueAtTime(gain, t0 + 0.005);
		g.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
		o.connect(g).connect(masterRef.current || ctx.destination);
		o.start(t0);
		o.stop(t0 + duration + 0.02);
	};

	const playSelect = () => blip(760, 0.075, "square", 0.06);
	const playDeselect = () => blip(420, 0.070, "triangle", 0.05);

	// typewriter
	const playTypeTick = () => {
		const now = performance.now();
		if (now - lastTickRef.current < 25) return; // limiter ~40 Hz
		lastTickRef.current = now;
		const base = 2600 + Math.random() * 900; // 2.6–3.5 kHz
		blip(base, 0.018, "square", 0.035);
	};

	const playTypeEnd = () => blip(1200, 0.12, "sine", 0.05);

	const playTimeUp = () => {
		const ctx = ensureAudio();
		if (!ctx) return;

		const t0 = ctx.currentTime;

		// Oscillatori: corpo + sub
		const oscA = ctx.createOscillator(); // corpo
		const oscB = ctx.createOscillator(); // sub
		oscA.type = "sawtooth";
		oscB.type = "square";

		// LFO per un wobble leggero (allarme più “vivo”)
		const lfo = ctx.createOscillator();
		const lfoGain = ctx.createGain();
		lfo.frequency.setValueAtTime(7.5, t0);    // ~7.5 Hz
		lfoGain.gain.setValueAtTime(22, t0);      // profondità mod
		lfo.connect(lfoGain);
		lfoGain.connect(oscA.frequency);

		// Low-pass per scurire
		const filt = ctx.createBiquadFilter();
		filt.type = "lowpass";
		filt.frequency.setValueAtTime(900, t0);
		filt.Q.setValueAtTime(0.0001, t0);

		// Envelope ampiezza
		const gain = ctx.createGain();
		const dur = 1; // ~0.6s richiesti
		gain.gain.setValueAtTime(0.0001, t0);
		gain.gain.linearRampToValueAtTime(0.16, t0 + 0.015);   // attacco rapido
		gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);

		// Frequenze base (cupissimo)
		const fA = 360; // corpo
		const fB = 180; // sub
		oscA.frequency.setValueAtTime(fA, t0);
		oscB.frequency.setValueAtTime(fB, t0);

		// Routing
		oscA.connect(filt);
		oscB.connect(filt);
		filt.connect(gain).connect(masterRef.current || ctx.destination);

		// Run/Stop
		oscA.start(t0);
		oscB.start(t0);
		lfo.start(t0);
		oscA.stop(t0 + dur + 0.02);
		oscB.stop(t0 + dur + 0.02);
		lfo.stop(t0 + dur + 0.02);
	};

	// useSfx.jsx (aggiungi in fondo ai metodi esistenti)
	const playWarn = () => {
		const ctx = ensureAudio();
		if (!ctx) return;

		const t0 = ctx.currentTime;

		// Oscillatori: uno saw "corposo" e uno square sub per aggiungere pancia
		const oscA = ctx.createOscillator();
		const oscB = ctx.createOscillator();
		const gain = ctx.createGain();
		const filt = ctx.createBiquadFilter();

		// Timbro più cupo: low-pass e inviluppo corto ma presente
		filt.type = "lowpass";
		filt.frequency.setValueAtTime(1000, t0); // taglia alte → suono scuro
		filt.Q.setValueAtTime(0.0001, t0);

		oscA.type = "sawtooth";
		oscB.type = "square";

		// Pitch sweep discendente leggero (allarme/buzzer feel)
		const fStart = 420;           // Hz
		const fEnd = 360;           // Hz (~150 ms)
		const dur = 0.20;          // durata totale beep
		const sweep = 0.15;          // durata sweep

		oscA.frequency.setValueAtTime(fStart, t0);
		oscA.frequency.exponentialRampToValueAtTime(fEnd, t0 + sweep);

		// Sotto-armonica per dare "pancia"
		oscB.frequency.setValueAtTime(fStart * 0.5, t0);        // ~210 Hz
		oscB.frequency.exponentialRampToValueAtTime(fEnd * 0.5, t0 + sweep);

		// Envelope: attacco rapido, decay esponenziale (niente code fastidiose)
		gain.gain.setValueAtTime(0.0001, t0);
		gain.gain.linearRampToValueAtTime(0.09, t0 + 0.012);    // attacco
		gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);

		// Routing
		oscA.connect(filt);
		oscB.connect(filt);
		filt.connect(gain);
		gain.connect(masterRef.current || ctx.destination);
		//gain.connect(ctx.destination);

		// Run
		oscA.start(t0);
		oscB.start(t0);
		oscA.stop(t0 + dur + 0.02);
		oscB.stop(t0 + dur + 0.02);
	};

	// Cache per il buffer di rumore
	const noiseBufRef = useRef(null);
	const getNoiseBuffer = (ctx) => {
		if (noiseBufRef.current) return noiseBufRef.current;
		const len = Math.floor(ctx.sampleRate * 2.0); // 2 secondi di rumore
		const buf = ctx.createBuffer(1, len, ctx.sampleRate);
		const data = buf.getChannelData(0);
		for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
		noiseBufRef.current = buf;
		return buf;
	};

	// ⭐ Swoosh ~1.2s: rumore band-pass con sweep e inviluppo
	const playSwoosh = ({ duration = 1.2, reverse = false } = {}) => {
		const ctx = ensureAudio(); if (!ctx) return;
		const t0 = ctx.currentTime;

		// sorgente rumore
		const src = ctx.createBufferSource();
		src.buffer = getNoiseBuffer(ctx);
		src.playbackRate.setValueAtTime(1.0 + (Math.random() * 0.1 - 0.05), t0); // micro-variazione

		// filtro band-pass con sweep della frequenza centrale
		const bp = ctx.createBiquadFilter();
		bp.type = "bandpass";
		// sweep up (default) o down (reverse)
		const fStart = reverse ? 12000 : 250;
		const fEnd = reverse ? 250 : 12000;
		bp.frequency.setValueAtTime(fStart, t0);
		// exponential ramp per “whoosh” naturale
		bp.frequency.exponentialRampToValueAtTime(fEnd, t0 + duration * 0.85);
		bp.Q.setValueAtTime(0.9, t0);

		// leggero low-pass per arrotondare le alte più dure
		const lp = ctx.createBiquadFilter();
		lp.type = "lowpass";
		lp.frequency.setValueAtTime(9000, t0);

		// inviluppo di ampiezza
		const g = ctx.createGain();
		g.gain.setValueAtTime(0.0001, t0);
		g.gain.linearRampToValueAtTime(0.18, t0 + 0.06);              // attack
		g.gain.exponentialRampToValueAtTime(0.08, t0 + duration * 0.5); // body
		g.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);   // tail

		// lieve movimento stereo (fa “aria” senza distrarre)
		const pan = ctx.createStereoPanner?.();
		const dest = pan ? pan : g; // fallback se no StereoPannerNode
		if (pan) {
			const p = (Math.random() * 0.6) - 0.3; // -0.3..0.3
			pan.pan.setValueAtTime(p, t0);
			pan.pan.linearRampToValueAtTime(-p, t0 + duration);
		}

		// routing
		src.connect(bp); bp.connect(lp); lp.connect(g);
		(pan ? g.connect(pan) : g).connect(ctx.destination);

		// run/stop
		src.start(t0);
		src.stop(t0 + duration + 0.05);
	};

	// Card flip effect: short percussive pitch + filtered noise flutter
	const playCardFlip = ({ duration = 0.24 } = {}) => {
		const ctx = ensureAudio();
		if (!ctx) return;
		const t0 = ctx.currentTime;

		// short pitched body
		const osc = ctx.createOscillator();
		const ogain = ctx.createGain();
		osc.type = 'triangle';
		osc.frequency.setValueAtTime(1200, t0);
		osc.frequency.exponentialRampToValueAtTime(650, t0 + 0.06);

		ogain.gain.setValueAtTime(0.0001, t0);
		ogain.gain.linearRampToValueAtTime(0.08, t0 + 0.008);
		ogain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);

		osc.connect(ogain);
		ogain.connect(masterRef.current || ctx.destination);

		// filtered noise flutter (paper/airy component)
		const src = ctx.createBufferSource();
		src.buffer = getNoiseBuffer(ctx);
		src.playbackRate.setValueAtTime(1.0 + (Math.random() * 0.15 - 0.075), t0);

		const bp = ctx.createBiquadFilter();
		bp.type = 'bandpass';
		bp.frequency.setValueAtTime(2200, t0);
		bp.frequency.exponentialRampToValueAtTime(700, t0 + duration * 0.9);
		bp.Q.setValueAtTime(0.7, t0);

		const ng = ctx.createGain();
		ng.gain.setValueAtTime(0.0001, t0);
		ng.gain.linearRampToValueAtTime(0.06, t0 + 0.01);
		ng.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);

		src.connect(bp);
		bp.connect(ng);
		ng.connect(masterRef.current || ctx.destination);

		// start/stop
		osc.start(t0);
		src.start(t0);
		osc.stop(t0 + duration + 0.02);
		src.stop(t0 + duration + 0.02);
	};

	// Snap secco di cartoncino: piccolo incipit + snap digitale clean
	const playCardSnap = ({ duration = 0.14 } = {}) => {
		const ctx = ensureAudio();
		if (!ctx) return;
		const t0 = ctx.currentTime;

		// sorgente di rumore condivisa
		const src = ctx.createBufferSource();
		src.buffer = getNoiseBuffer(ctx);
		src.playbackRate.setValueAtTime(
			1.0 + (Math.random() * 0.12 - 0.06),
			t0
		);

		// --- INCIPIT: colpetto morbido, più "corpo" (mid) ---
		const preBp = ctx.createBiquadFilter();
		preBp.type = "bandpass";
		preBp.frequency.setValueAtTime(900, t0);
		preBp.Q.setValueAtTime(0.8, t0);

		const preGain = ctx.createGain();
		preGain.gain.setValueAtTime(0.0001, t0);
		preGain.gain.linearRampToValueAtTime(0.035, t0 + 0.012); // piccolo bump
		preGain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.07);

		// --- SNAP: transiente secca, più brillante e veloce ---
		const snapBp = ctx.createBiquadFilter();
		snapBp.type = "bandpass";
		snapBp.frequency.setValueAtTime(3200, t0 + 0.015);
		snapBp.Q.setValueAtTime(1.1, t0);

		const snapGain = ctx.createGain();
		snapGain.gain.setValueAtTime(0.0001, t0 + 0.01);
		snapGain.gain.linearRampToValueAtTime(0.11, t0 + 0.028); // picco netto
		snapGain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);

		// somma dei due (incipit + snap) su un gain finale
		const out = ctx.createGain();
		out.gain.setValueAtTime(1.0, t0);
		out.connect(masterRef.current || ctx.destination);

		// routing rumore
		src.connect(preBp);
		src.connect(snapBp);
		preBp.connect(preGain).connect(out);
		snapBp.connect(snapGain).connect(out);

		// --- piccola componente pitchata per dare "click" digitale ---
		const osc = ctx.createOscillator();
		const ogain = ctx.createGain();
		osc.type = "triangle";
		osc.frequency.setValueAtTime(1800, t0 + 0.015);
		osc.frequency.exponentialRampToValueAtTime(950, t0 + 0.05);

		ogain.gain.setValueAtTime(0.0001, t0 + 0.015);
		ogain.gain.linearRampToValueAtTime(0.08, t0 + 0.03);
		ogain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);

		osc.connect(ogain).connect(masterRef.current || ctx.destination);

		// start/stop
		src.start(t0);
		osc.start(t0 + 0.012);

		const stopAt = t0 + duration + 0.06;
		src.stop(stopAt);
		osc.stop(stopAt);
	};

	const tadaAudioRef = useRef(null);

	const ensureAudioElement = useCallback(() => {
		if (!tadaAudioRef.current) {
			tadaAudioRef.current = new Audio("/react-app/media/tada.mp3");
		}
		return tadaAudioRef.current;
	}, []);

	const playTada = useCallback(() => {
		const audio = ensureAudioElement();
		if (!audio) return;

		audio.currentTime = 0;
		audio.volume = volume;      // se hai già uno state volume globale
		if (muted) return;          // se gestisci muted
		audio.play();
	}, [ensureAudioElement, muted, volume]);


	return {
		muted,
		setMuted,
		volume,
		setVolume, // 👈 slider friendly
		ensureAudio,
		playSelect,
		playDeselect,
		playTypeTick,
		playTypeEnd,
		playWarn,
		playTimeUp,
		playSwoosh,
		playCardFlip,
		playCardSnap,
		playTada 
	};
}
